export function parsePriceToMinor(price) {
  if (price == null || price === "") return 0;
  const n = parseFloat(String(price).replace(/[^\d.]/g, ""));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

export function minorToAmount(minor) {
  return (Number(minor || 0) / 100);
}

export function formatMoney(minor, currency = "INR") {
  const amount = minorToAmount(minor);
  if (currency === "INR") {
    return `₹${amount.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  }
  return `${currency} ${amount.toFixed(2)}`;
}

export function formatQuotationLabel(number) {
  return `#QUOT-${number}`;
}

const ONES = [
  "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen", "Eighteen", "Nineteen",
];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function wordsBelow100(n) {
  if (n < 20) return ONES[n];
  const ten = Math.floor(n / 10);
  const one = n % 10;
  return `${TENS[ten]}${one ? ` ${ONES[one]}` : ""}`.trim();
}

function wordsBelow1000(n) {
  if (n < 100) return wordsBelow100(n);
  const hundred = Math.floor(n / 100);
  const rest = n % 100;
  return `${ONES[hundred]} Hundred${rest ? ` ${wordsBelow100(rest)}` : ""}`;
}

function integerToWords(n) {
  if (!Number.isFinite(n) || n < 0) return "";
  if (n === 0) return "Zero";

  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const hundred = n % 1000;
  const parts = [];

  if (crore) parts.push(`${wordsBelow1000(crore)} Crore`);
  if (lakh) parts.push(`${wordsBelow100(lakh)} Lakh`);
  if (thousand) parts.push(`${wordsBelow100(thousand)} Thousand`);
  if (hundred) parts.push(wordsBelow1000(hundred));

  return parts.join(" ");
}

export function amountToWords(minor, currency = "INR") {
  const amount = minorToAmount(minor);
  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);
  const rupeeWords = integerToWords(rupees);
  const prefix = currency === "INR" ? "INR" : currency;

  if (!paise) {
    return `${prefix}: ${rupeeWords} Only`;
  }
  return `${prefix}: ${rupeeWords} and ${integerToWords(paise)} Paise Only`;
}

export function todayDateInputValue() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function computeDiscountMinor(subtotalMinor, discount) {
  if (!discount || !discount.type) return 0;
  if (discount.type === "fixed") {
    return Math.min(subtotalMinor, Math.max(0, parseInt(discount.value_minor, 10) || parsePriceToMinor(discount.value) || 0));
  }
  if (discount.type === "rate") {
    const rate = Math.max(0, Number(discount.value) || 0);
    return Math.round(subtotalMinor * rate / 100);
  }
  return 0;
}

function computeChargesMinor(subtotalMinor, charges) {
  if (!Array.isArray(charges)) return 0;
  return charges.reduce((sum, charge) => {
    if (!charge) return sum;
    if (charge.type === "rate") {
      const rate = Math.max(0, Number(charge.value) || 0);
      return sum + Math.round(subtotalMinor * rate / 100);
    }
    return sum + Math.max(0, parseInt(charge.value_minor, 10) || parsePriceToMinor(charge.value) || 0);
  }, 0);
}

function computeExtraTaxMinor(taxableBaseMinor, taxes) {
  if (!Array.isArray(taxes)) return 0;
  return taxes.reduce((sum, tax) => {
    const rate = Math.max(0, Number(tax?.rate_percent ?? tax?.rate) || 0);
    return sum + Math.round(taxableBaseMinor * rate / 100);
  }, 0);
}

export function normalizeLineItem(raw, index = 0) {
  const quantity = Math.max(1, parseInt(raw.quantity, 10) || 1);
  const unitPriceMinor = raw.unit_price_minor != null
    ? Math.max(0, parseInt(raw.unit_price_minor, 10) || 0)
    : parsePriceToMinor(raw.unit_price ?? raw.price);
  const lineTotalMinor = quantity * unitPriceMinor;
  const taxRate = raw.tax_rate != null && raw.tax_rate !== "" ? Number(raw.tax_rate) : null;
  const taxAmountMinor = taxRate != null && taxRate > 0
    ? Math.round(lineTotalMinor * taxRate / 100)
    : Math.max(0, parseInt(raw.tax_amount_minor, 10) || 0);

  return {
    retailer_id: raw.retailer_id || null,
    catalog_product_id: raw.catalog_product_id || raw.product_id || null,
    name: String(raw.name || "").trim(),
    description: raw.description ? String(raw.description).trim() : null,
    quantity,
    unit_price_minor: unitPriceMinor,
    line_total_minor: lineTotalMinor,
    tax_rate: taxRate,
    tax_amount_minor: taxAmountMinor,
    image_url: raw.image_url || null,
    sort_order: raw.sort_order != null ? raw.sort_order : index,
  };
}

export function computeQuotationTotals(lineItemsInput, adjustmentsInput = {}) {
  const lineItems = (Array.isArray(lineItemsInput) ? lineItemsInput : [])
    .map((item, index) => normalizeLineItem(item, index))
    .filter((item) => item.name);

  const subtotalMinor = lineItems.reduce((sum, item) => sum + item.line_total_minor, 0);
  const lineTaxMinor = lineItems.reduce((sum, item) => sum + item.tax_amount_minor, 0);

  const adjustments = {
    discount: adjustmentsInput.discount || null,
    charges: Array.isArray(adjustmentsInput.charges) ? adjustmentsInput.charges : [],
    taxes: Array.isArray(adjustmentsInput.taxes) ? adjustmentsInput.taxes : [],
  };

  const discountMinor = computeDiscountMinor(subtotalMinor, adjustments.discount);
  const chargesMinor = computeChargesMinor(subtotalMinor, adjustments.charges);
  const taxableBaseMinor = Math.max(0, subtotalMinor - discountMinor + chargesMinor);
  const extraTaxMinor = computeExtraTaxMinor(taxableBaseMinor, adjustments.taxes);
  const taxMinor = lineTaxMinor + extraTaxMinor;
  const totalMinor = taxableBaseMinor + taxMinor;

  return {
    lineItems,
    adjustments,
    subtotalMinor,
    discountMinor,
    chargesMinor,
    taxMinor,
    totalMinor,
  };
}

export const QUOTATION_STATUSES = [
  { value: "created", label: "Created", color: "default" },
  { value: "sent", label: "Sent", color: "info" },
  { value: "accepted", label: "Accepted", color: "success" },
  { value: "invoiced", label: "Invoiced", color: "primary" },
  { value: "expired", label: "Expired", color: "warning" },
  { value: "converted", label: "Converted", color: "secondary" },
];

export function catalogProductToLineItem(product, quantity = 1) {
  const retailerId = product.retailer_id || product.catalog_product_id || product.id;
  const unitPriceMinor = parsePriceToMinor(product.price ?? product.sale_price);
  return normalizeLineItem({
    retailer_id: retailerId,
    catalog_product_id: product.catalog_product_id || product.id || retailerId,
    name: product.name || product.title || "Product",
    description: product.description || null,
    quantity,
    unit_price_minor: unitPriceMinor,
    image_url: product.image_url || null,
    tax_rate: null,
  });
}
